// DailySummary.tsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import DailySummaryTable from './Table-widgets/DailySummaryTable';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import useConfig from '../../../hooks/useConfig';
import { fetchDailySummaryTier1, fetchDailySummaryTier2, selectDowntimeDaily } from './slice';
import { dailySummaryColumns, mapDailySummaryToTableData } from './Table-widgets/DailySummaryTableData';
import './DailySummary.scss';
import { useParams } from 'react-router-dom';
import { DailySummaryItem } from './types';
import ColumnSelector from '../../../components/column-selector/colum-selector';
import Loader from '../../common/Loader/Loader';
import DateRangePicker from '../../../components/date-range-picker/DateRangePicker';
import { formatDate } from '../../../utilities/dateHelpers';
import { fetchColumnsData } from '../../../dispatchers/ColumnSelectorDispatcher';
import { DashboardConfig, saveUserPerferences } from '../../../dispatchers/UserPreferenceDispatcher';
import ExportTable from '../../../components/common/export-table/ExportTable';
import TableSearch from '../../../components/table-search/TableSearch';
import { formatNumber } from '../../../utilities/Helpers';

interface DailySummaryProps {
  tableName?: string;
}

const ACRONYMS = ['MOP', 'ESP', 'BHP', 'GLR', 'GOR', 'THP', 'BHT', 'VFD'];

const toSentenceCasePreserveAcronyms = (label?: string | null) => {
  if (!label) return '';
  const trimmed = String(label).trim();
  const upper = trimmed.toUpperCase();
  if (ACRONYMS.includes(upper)) return upper;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

export const formatDateForExport = (iso?: string | null): string => {
  return formatDate(iso);
};

const formatShortDate = (d?: Date | string | null) => {
  return formatDate(d);
};

type ColumnConfig = {
  id: string;
  Header: string;
  isVisible: boolean;
  key: string;
};

const DailySummary: React.FC<DailySummaryProps> = ({ tableName = 'Downtime' }) => {
  const dispatch = useAppDispatch();
  const { enablePumpChecker } = useConfig();
  const downtime = useAppSelector(selectDowntimeDaily);
  const industryApplicationId = useAppSelector((state: any) => state.AssetAnalysis.industryApplicationId);

  const effectivePumpChecker = enablePumpChecker && [4, 7].includes(industryApplicationId);
  const { assetId } = useParams<{ assetId: string }>();
  const wellId = useAppSelector((state: any) => state.WellTestIdSlice?.data?.WellId);

  const tableDataRaw = useMemo(() => downtime.items ?? [], [downtime.items]);
  const tableData = useMemo(() => mapDailySummaryToTableData(tableDataRaw), [tableDataRaw]);

  const tier = effectivePumpChecker ? 2 : 1;
  const columns = useMemo(() => dailySummaryColumns(tier), [tier]);

  const [searchTerm, setSearchTerm] = useState('');
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [localColumns, setLocalColumns] = useState<ColumnConfig[]>([]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [isConfigCheckBoxClicked, setIsConfigCheckBoxClicked] = useState(false);
  const [fixedColumns, setFixedColumns] = useState<string[]>([]);
  const [taskWidgetProps, setTaskWidgetProps] = useState<{ columns?: any[]; fixedColumns?: any[] }>({});

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const datePickerRef = useRef<HTMLDivElement | null>(null);

  const downtimePageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const downtimeWidget = downtimePageData?.find((obj: any) => obj.key === 'downtime-dailysummarytable');

  useEffect(() => {
    dispatch(fetchColumnsData('Downtime'));
  }, [dispatch]);

  const getData = useCallback(() => {
    if (enablePumpChecker === null) return;

    if (effectivePumpChecker && wellId !== undefined) {
      dispatch(fetchDailySummaryTier2({ wellId }));
    } else if (!effectivePumpChecker && assetId !== undefined) {
      dispatch(fetchDailySummaryTier1({ assetId }));
    }
  }, [dispatch, enablePumpChecker, effectivePumpChecker, wellId, assetId]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (!showDatePicker) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  useEffect(() => {
    const props = downtimeWidget?.widgetProperty?.widgetProperties || {};
    setTaskWidgetProps(props);

    const fixed: string[] = [...(props.fixedColumns?.map((col: any) => String(col.key)) || [])];
    setFixedColumns(fixed);

    const configuredVisible: string[] =
      (props.columns?.filter((c: any) => c.selected).map((c: any) => String(c.key)) as string[]) || [];

    const allConfigurable: string[] =
      (props.columns?.map((c: any) => String(c.key)) as string[]) ||
      columns.map((c) => String((c.accessor as string) || (c as any).id));

    const initialVisible = configuredVisible.length
      ? configuredVisible
      : visibleColumns.length
        ? visibleColumns
        : allConfigurable;

    setVisibleColumns(initialVisible);

    const localResolved: ColumnConfig[] = allConfigurable.map((id: string) => {
      const def = columns.find((c) => String((c.accessor as string) || (c as any).id) === id);
      const headerRaw = (def?.Header as string) || id;
      return {
        id,
        Header: toSentenceCasePreserveAcronyms(headerRaw),
        isVisible: initialVisible.includes(id),
        key: id,
      };
    });

    setLocalColumns(localResolved);
  }, [downtimeWidget, columns]); // intentionally not depending on visibleColumns

  const handleSaveConfig = (config: boolean) => setIsConfigCheckBoxClicked(config);

  const resetHandler = useCallback(() => {
    if (!taskWidgetProps?.columns?.length) return;
    const configurableColumnsAfterReset = taskWidgetProps.columns.map((col: any) => String(col.key));
    const visibleColumnsAfterReset = [...configurableColumnsAfterReset];

    setVisibleColumns(visibleColumnsAfterReset);

    const newLocal = configurableColumnsAfterReset
      .filter((id: string) => !fixedColumns.includes(id))
      .map((id: string) => ({
        id,
        Header: toSentenceCasePreserveAcronyms(
          (columns.find((c) => ((c.accessor as string) || (c as any).id) === id)?.Header as string) || id,
        ),
        isVisible: visibleColumnsAfterReset.includes(id),
        key: id,
      }));
    setLocalColumns(newLocal);
  }, [taskWidgetProps, fixedColumns, columns]);

  const handleApplyClicked = useCallback(
    async (staged?: ColumnConfig[]) => {
      const current = staged && staged.length ? staged : localColumns;

      const mandatoryColumns = (downtimeWidget?.widgetProperty?.widgetProperties?.fixedColumns || []).map(
        (c: any) => c.id,
      );

      const selectedConfigurable = current.filter((col) => col.isVisible).map((col) => col.id);
      const newVisible = [...mandatoryColumns, ...selectedConfigurable.filter((id) => !mandatoryColumns.includes(id))];

      // update visible columns immediately
      setVisibleColumns(newVisible);

      const configurableColumns = current.filter((item) => !mandatoryColumns.includes(item.id));
      const transformedArray = configurableColumns.map((item, index) => ({
        order: index + 1,
        label: item.Header,
        key: item.id,
        selected: item.isVisible,
      }));

      if (isConfigCheckBoxClicked) {
        try {
          const transformedWidget: DashboardConfig = {
            DashboardName: downtimeWidget?.widgetProperty?.dashboard ?? '',
            WidgetName: downtimeWidget?.widgetProperty?.key ?? 'downtime',
            PropertyType: 'Columns',
            Preferences: transformedArray.map((col) => ({
              Order: col.order,
              Label: col.label,
              Key: col.key,
              Selected: col.selected,
              Visible: true,
            })),
          };
          await dispatch(saveUserPerferences(transformedWidget));
        } catch (err) {
          console.error('Error saving column configuration', err);
        }
      }

      // keep localColumns in sync (ordered)
      setLocalColumns(current.map((c) => ({ ...c })));
    },
    [localColumns, downtimeWidget, isConfigCheckBoxClicked, dispatch],
  );

  const filteredData = useMemo(() => {
    let rows = tableData;

    // date range filter (if set)
    if (startDate || endDate) {
      const s = startDate ? new Date(startDate) : null;
      const e = endDate ? new Date(endDate) : null;

      rows = rows.filter((r: DailySummaryItem) => {
        if (!r.date) return false;
        const d = new Date(r.date);
        if (s && d < startOfDay(s)) return false;
        if (e && d > endOfDay(e)) return false;
        return true;
      });
    }

    if (!searchTerm) return rows;

    const lower = searchTerm.toLowerCase().trim();

    // determine if the input is numeric and how many decimals it has
    const cleaned = lower.replace(/,/g, ''); // allow formatted numbers like "1,234.56"
    const isNumericSearch = cleaned !== '' && !Number.isNaN(Number(cleaned));
    const inputDecimals = ((): number => {
      const dot = cleaned.indexOf('.');
      if (dot === -1) return 0;
      return cleaned.length - dot - 1;
    })();

    return rows.filter((r: DailySummaryItem) => {
      // displayed/formatted fields (2 decimals)
      const dateStr = formatDateForExport(r.date).toLowerCase();
      const rawDateStr = String(r.date ?? '').toLowerCase();

      const hoursStr = r.hours != null && !Number.isNaN(Number(r.hours)) ? Number(r.hours).toFixed(2) : '';
      const lostOilStr = r.lostOil != null && !Number.isNaN(Number(r.lostOil)) ? Number(r.lostOil).toFixed(2) : '';
      const lostGasStr = r.lostGas != null && !Number.isNaN(Number(r.lostGas)) ? Number(r.lostGas).toFixed(2) : '';
      const lostWaterStr =
        r.lostWater != null && !Number.isNaN(Number(r.lostWater)) ? Number(r.lostWater).toFixed(2) : '';
      const downtimePercentStr =
        r.downtimePercent != null && !Number.isNaN(Number(r.downtimePercent))
          ? Number(r.downtimePercent).toFixed(2)
          : '';
      const downtimeEventsStr = r.downtimeEvents != null ? String(r.downtimeEvents) : '';

      const mopStr = String(r.mop ?? '').toLowerCase();

      if (isNumericSearch) {
        if (inputDecimals > 2) {
          return dateStr.includes(lower) || rawDateStr.includes(lower) || mopStr.includes(lower);
        }

        return (
          dateStr.includes(lower) ||
          rawDateStr.includes(lower) ||
          hoursStr.includes(lower) ||
          lostOilStr.includes(lower) ||
          lostGasStr.includes(lower) ||
          lostWaterStr.includes(lower) ||
          downtimePercentStr.includes(lower) ||
          downtimeEventsStr.includes(lower) ||
          mopStr.includes(lower)
        );
      }

      return (
        dateStr.includes(lower) ||
        rawDateStr.includes(lower) ||
        hoursStr.includes(lower) ||
        lostOilStr.includes(lower) ||
        lostGasStr.includes(lower) ||
        lostWaterStr.includes(lower) ||
        downtimePercentStr.includes(lower) ||
        downtimeEventsStr.includes(lower) ||
        mopStr.includes(lower)
      );
    });
  }, [tableData, searchTerm, startDate, endDate]);

  const pick = (obj: any, candidates: string[]) => {
    if (!obj) return undefined;
    for (const k of candidates) {
      if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== undefined && obj[k] !== null) {
        return obj[k];
      }
    }
    return undefined;
  };

  const formatDateAny = (val: any) => {
    return formatDate(val);
  };

  const EXPORT_COLUMN_MAP: Record<string, any> = {
    date: {
      key: 'Date',
      header: toSentenceCasePreserveAcronyms('Date'),
      accessor: (row: any) =>
        formatDateAny(
          pick(row.original, [
            'date',
            'Date',
            'dateLocalString',
            'IndividualDateLocalString',
            'IndividualDate',
            'dateLocal',
          ]),
        ),
    },
    hours: {
      key: 'Hours',
      header: toSentenceCasePreserveAcronyms('Hours'),
      accessor: (row: any) => formatNumber(pick(row.original, ['hours', 'Hours', 'TotalHours']) ?? 0, 2),
    },
    mop: {
      key: 'MOP',
      header: toSentenceCasePreserveAcronyms('MOP'),
      accessor: (row: any) =>
        toSentenceCasePreserveAcronyms(pick(row.original, ['mop', 'MOP', 'MethodOfProduction']) ?? 'ESP'),
    },
    lostoil: {
      key: 'LostOil',
      header: toSentenceCasePreserveAcronyms('Lost Oil (stb)'),
      accessor: (row: any) => formatNumber(pick(row.original, ['lostOil', 'LostOil']) ?? 0, 2),
    },
    lostgas: {
      key: 'LostGas',
      header: toSentenceCasePreserveAcronyms('Lost Gas'),
      accessor: (row: any) => formatNumber(pick(row.original, ['lostGas', 'LostGas']) ?? 0, 2),
    },
    lostwater: {
      key: 'LostWater',
      header: toSentenceCasePreserveAcronyms('Lost Water'),
      accessor: (row: any) => formatNumber(pick(row.original, ['lostWater', 'LostWater']) ?? 0, 2),
    },
    downtimepercent: {
      key: 'DowntimePercent',
      header: toSentenceCasePreserveAcronyms('Downtime %'),
      accessor: (row: any) => formatNumber(pick(row.original, ['downtimePercent']) ?? 0, 2),
    },
    downtimeevents: {
      key: 'DowntimeEvents',
      header: toSentenceCasePreserveAcronyms('Downtime Events'),
      accessor: (row: any) => String(pick(row.original, ['downtimeEvents']) ?? ''),
    },
  };

  const tableRowsForExport = filteredData.map((r: DailySummaryItem) => ({ original: r }));

  // --- Corrected mapping tables (use table accessors / ids as canonical keys) ---
  const COLUMN_ID_MAP: Record<string, { header: string; accessor: string }> = {
    date: { header: 'Date', accessor: 'date' },
    hours: { header: 'Hours', accessor: 'hours' },
    mop: { header: 'MOP', accessor: 'mop' },
    lostoil: { header: 'Lost Oil', accessor: 'lostOil' },
    lostgas: { header: 'Lost Gas', accessor: 'lostGas' },
    lostwater: { header: 'Lost Water', accessor: 'lostWater' },
    downtimepercent: { header: 'Downtime %', accessor: 'downtimePercent' },
    downtimeevents: { header: 'Downtime Events', accessor: 'downtimeEvents' },
    '': { header: 'MOP', accessor: 'mop' },
    noofdowntimes: { header: 'Downtime events', accessor: 'downtimeEvents' },
    downtimepercentage: { header: 'Downtime %', accessor: 'downtimePercent' },
  };

  // Robust checkId: use mapping when possible, fallback to original id
  const normalizeKey = (s?: string) => (s || '').toString().toLowerCase();

  const checkId = (id: string, isHeader = false): string => {
    const key = normalizeKey(id);
    const mapping = COLUMN_ID_MAP[key];
    if (!mapping) {
      // fallback: return raw id (preserve header or accessor as needed)
      return isHeader ? String(id) : String(id);
    }
    return isHeader ? mapping.header : mapping.accessor;
  };

  // ---------- Build effectiveVisibleIds (normalized to accessor strings) ----------
  const effectiveVisibleIds = useMemo(() => {
    const validIds = new Set(
      columns.map((c) => {
        const acc = ((c.accessor as string) || (c as any).id || '').toString();
        return normalizeKey(acc);
      }),
    );

    const normalizedVisible = visibleColumns
      .map((id) => {
        // if the stored id maps to a known accessor, use mapping; otherwise use the id itself
        const mapped = COLUMN_ID_MAP[normalizeKey(String(id))]?.accessor || String(id);
        return normalizeKey(mapped);
      })
      .filter((n) => !!n);

    const filtered = normalizedVisible.filter((n) => validIds.has(n));
    return filtered.length ? filtered : Array.from(validIds);
  }, [visibleColumns, columns]);

  // Build effectiveColumns using normalized lookup by accessor (lowercase)
  const effectiveColumns = useMemo(() => {
    const defMap = new Map<string, any>();
    for (const c of columns) {
      const id = ((c.accessor as string) || (c as any).id || '').toString();
      defMap.set(normalizeKey(id), c);
    }

    return effectiveVisibleIds.map((id) => defMap.get(normalizeKey(id))).filter((c): c is any => !!c);
  }, [columns, effectiveVisibleIds]);

  const exportColumns = useMemo(() => {
    return effectiveColumns
      .map((col: any) => {
        const accessor = ((col.accessor as string) || (col.id as string) || '').toLowerCase();

        return EXPORT_COLUMN_MAP[accessor];
      })
      .filter(Boolean);
  }, [effectiveColumns]);

  const openColumnSelector = () => {
    const copy = localColumns.length
      ? localColumns.map((c) => ({ ...c }))
      : columns.map((col) => {
        const id = ((col.accessor as string) || (col as any).id).toString();
        const headerRaw = (col.Header as string) || id;
        return {
          id,
          Header: toSentenceCasePreserveAcronyms(headerRaw),
          isVisible: visibleColumns.includes(id),
          key: id,
        } as ColumnConfig;
      });
    setLocalColumns(copy.map((c) => ({ ...c, key: String(c.key ?? c.id) })));
    setShowColumnSelector(true);
  };

  const onLocalColumnsUpdate = (updated: ColumnConfig[]) => {
    setLocalColumns(updated.map((c) => ({ ...c, key: String(c.key ?? c.id) })));
  };

  const showDateRangeButtonLabel =
    startDate && endDate ? `${formatShortDate(startDate)} - ${formatShortDate(endDate)}` : 'Select date range';

  return (
    <div className='daily-summary-root'>
      <div className='daily-summary-toolbar-wrapper' style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div className='toolbar-left' style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div>
            <button
              className='flex w-[118px] h-[40px] pt-[10px] pr-[16px] pb-[10px] pl-[16px] gap-[8px] bg-[#131f2e] rounded-[8px] border border-[#4a5463] table-button'
              onClick={openColumnSelector}
              type='button'
            >
              <span className='ico-colom' />
              <span style={{ fontWeight: 600 }}>Columns</span>
            </button>
            {showColumnSelector && (
              <ColumnSelector
                key='column-selector'
                columnsData={
                  downtime.loading
                    ? []
                    : (localColumns || []).map((c) => {
                      return {
                        id: String(c.id ?? c.key),
                        Header: checkId(c.id, true),
                        isVisible: !!c.isVisible,
                        key: String(c.key ?? c.id),
                      };
                    })
                }
                onUpdateColumns={(updated: any[]) => {
                  const mapped: ColumnConfig[] =
                    (updated || []).map((u: any) => ({
                      id: String(u.id ?? u.key),
                      Header: toSentenceCasePreserveAcronyms(u.Header ?? u.label ?? String(u.id ?? u.key)),
                      isVisible: !!(u.isVisible ?? u.selected),
                      key: String(u.key ?? u.id),
                    })) || [];
                  onLocalColumnsUpdate(mapped);
                }}
                onClose={() => setShowColumnSelector(false)}
                onApplyClickedInColumn={() => {
                  handleApplyClicked(localColumns);
                }}
                saveConfigHasbeenClicked={handleSaveConfig}
                resetToDefaultSystemClicked={resetHandler}
                tableName='downtime-dailysummarytable'
                isChartConfig={false}
              />
            )}
          </div>

          <div style={{ position: 'relative' }}>
            <button
              className='flex items-center h-[40px] px-[14px] gap-[8px] rounded-[8px] border border-[#4a5463] table-button bg-[#131f2e]'
              onClick={() => setShowDatePicker((s) => !s)}
              type='button'
            >
              <span className='ico-calendar' />
              <span style={{ fontWeight: 600, marginRight: '6px' }}>{showDateRangeButtonLabel}</span>
              {(startDate || endDate) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setStartDate(null);
                    setEndDate(null);
                  }}
                  type='button'
                  aria-label='Clear date range'
                  style={{
                    marginLeft: 8,
                    background: 'transparent',
                    border: 'none',
                    color: '#b8c5cc',
                    cursor: 'pointer',
                  }}
                >
                  ✕
                </button>
              )}
            </button>

            {showDatePicker && (
              <div ref={datePickerRef} style={{ position: 'absolute', zIndex: 9999, top: '21px', left: 0 }}>
                <DateRangePicker
                  onApply={({ startDate: s, endDate: e }) => {
                    setStartDate(s);
                    setEndDate(e);
                    setShowDatePicker(false);
                  }}
                  onCancel={() => setShowDatePicker(false)}
                  initialStartDate={startDate ?? undefined}
                  initialEndDate={endDate ?? undefined}
                  showTimeInputs={false}
                />
              </div>
            )}
          </div>

          <div className='downtime-export-button'>
            <ExportTable
              data={tableRowsForExport}
              fileName={`${tableName}`}
              sheetName={tableName}
              columns={exportColumns}
              disabled={!tableRowsForExport || tableRowsForExport.length === 0}
              rPermission={{ canExport: true }}
            />
          </div>
        </div>

        <TableSearch containerClasses='ml-auto !w-[290px]' onSearch={(searchText) => setSearchTerm(searchText)} />
      </div>

      <div className='daily-summary-tablewrap' aria-busy={downtime.loading}>
        {downtime.loading ? (
          <div className='isLoading'>
            <Loader />
          </div>
        ) : (
          <DailySummaryTable data={filteredData} columns={effectiveColumns} maxHeight='450px' />
        )}
      </div>
    </div>
  );
};

function startOfDay(d: Date) {
  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);
  return dd;
}
function endOfDay(d: Date) {
  const dd = new Date(d);
  dd.setHours(23, 59, 59, 999);
  return dd;
}

export default DailySummary;
